import { useEnv } from '../../../context/EnvironmentContext/EnvironmentContext';
import { useUserProfile } from '../../../context/UserProfileContext/UserProfileContext';
import { useCurrentLocale } from '../../../hooks/useCurrentLocale';
import { useSigninRedirect } from '../../../hooks/useSigninRedirect';
import { useFeature } from 'flagged';
import { useMemo } from 'react';
import { hasPermission } from '../../../helpers/helpers';
import { Permission } from '../../../utils/permissions';
import { useLocation } from 'react-router';
import { FeatureFlag } from '../../../constants/featureFlag';
import { App } from '@partnerconnect-monorepo/types/EnvironmentVariables';
import { useAuth } from 'react-oidc-context';

export const useNavbar = () => {
  const { signinRedirect } = useSigninRedirect();

  const { userProfile } = useUserProfile();

  const { isAuthenticated } = useAuth();

  const { languageCode, languages, setLocale } = useCurrentLocale();

  const canAccessInvoicing = useMemo(
    () =>
      hasPermission(
        userProfile?.permissions ?? [],
        Permission.CanAccessInvoicing
      ),
    [userProfile?.permissions]
  );

  const canAccessPartnershipsAndFinancesUsers = useMemo(
    () =>
      hasPermission(
        userProfile?.permissions ?? [],
        Permission.CanAccessPartnershipsUsers
      ),
    [userProfile?.permissions]
  );

  const location = useLocation();

  const isUsersManagementAccessed = useMemo(
    () =>
      location.pathname.includes('/users') ||
      location.pathname.includes('/partner-users'),
    [location]
  );

  const canAccessReportingUsers = useMemo(
    () =>
      hasPermission(
        userProfile?.permissions ?? [],
        Permission.CanViewPartnerProfile
      ),
    [userProfile?.permissions]
  );

  const canAccessReporting = useMemo(
    () =>
      hasPermission(
        userProfile?.permissions ?? [],
        Permission.CanViewMonthlyReports
      ),
    [userProfile?.permissions]
  );

  const canAccessPartnershipOpportunities = useMemo(
    () =>
      hasPermission(
        userProfile?.permissions ?? [],
        Permission.CanViewPartnershipOpportunity
      ),
    [userProfile?.permissions]
  );

  const canAccessSpotcheck = useMemo(
    () =>
      hasPermission(
        userProfile?.permissions ?? [],
        Permission.CanAccessSpotcheck
      ),
    [userProfile?.permissions]
  );

  const { appName } = useEnv();

  const isPartnershipsModuleAccessed = [
    App.Partnerships,
    App.Spotcheck
  ].includes(appName);

  const isPartnershipsOverviewPageEnabled = useFeature(
    FeatureFlag.PartnershipsOverviewPage
  ) as boolean;

  return {
    setLocale,
    languageCode,
    languages,
    signinRedirect,
    canAccessInvoicing,
    canAccessPartnershipsAndFinancesUsers,
    canAccessReportingUsers,
    isUsersManagementAccessed,
    canAccessReporting,
    canAccessPartnershipOpportunities,
    canAccessSpotcheck,
    isPartnershipsModuleAccessed,
    isAuthenticated,
    isPartnershipsOverviewPageEnabled
  };
};
